import React, {useEffect, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {Button, Dialog, ErrorBlock, Image, NavBar, Skeleton, Toast} from 'antd-mobile';
import {useQuery} from '@apollo/client';

import photoService, {PhotoType, PositionType} from 'pages/delivery/services/PhotoService';
import {useLocationStore} from 'store/locationStore';
import PhotoUploadCard from 'pages/delivery/components/PhotoUploadCard';
import MultiPhotoGalleryView from 'pages/delivery/components/MultiPhotoGalleryView';
import PhotoViewer from 'pages/delivery/components/PhotoViewer';
import {getBottomNavBarContainerStyle, getNavBarContainerStyle} from 'utils/deviceUtils';
import {NativePermissions} from 'pages/delivery/components/PhotoNativeBridge';
import {isAppVersionGreaterThanOrEqual, kNativeMultiImagePickerVersion} from 'utils/versionUtils';
import {openCamera} from 'utils/appRuntime';
import {uploadPhotosWithTimeout} from 'services/DeliveryDataService';
import {t} from 'i18n';
import {useTranslation} from 'react-i18next';
import {GetDspPackageDetailByTrackingNumberResponse, GetDspPackageDetailByTrackingNumberVariables, GET_DSP_PACKAGE_DETAIL} from "../graphql/queries/getPackageDetail";
import multiPhotoService from "../../delivery/services/MultiPhotoService";
import {DeliveredEventInput, handleDspDeliveredEvent} from "../../delivery/graphql/mutations/delivered";
import { DspPath } from 'enums/DeliveryNavigatePathEnum';
import MultiPhotoSelectorView from 'pages/delivery/components/MultiPhotoSelectorView';
import { InfoRow } from '../../../components/InfoRow';
import {formatStopNumber} from "../../../utils/stopNumberUtils";
import {formatTimestampToSecondsTime, timestampToDate} from "../../../utils/dateTimeUtils";
import OrderNoteItem from '../../../components/OrderNoteItem/OrderNoteItem';
import {isDspManager} from '../../../utils/auth';
import {PodReviewStatus} from '../../../utils/podStatusUtils';
import {maskPhoneNumber} from "../../../utils/phoneUtils";
import {
    GET_DELIVERY_METHOD_I18N_CONFIG,
    GetDeliveryMethodI18nConfigResponse
} from 'graphql/queries/getDeliveryMethodI18nConfig';
import DeliveryMethodDisplayCard from 'pages/delivery/components/DeliveryMethodDisplayCard';

const kMinSelectorPhotoLength = 3
const kMaxSelectorPhotoLength = 6

interface DspDeliveredPodProps {}

const DspParticularsPodPage: React.FC<DspDeliveredPodProps> = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [photos, setPhotos] = useState<Record<PositionType, PhotoType | undefined>>({} as Record<PositionType, PhotoType | undefined>);
    const [photosArray, setPhotosArray] = useState<PhotoType[]>([]);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showMultiPhotoSelector, setShowMultiPhotoSelector] = useState<boolean>(false);
    const { position } = useLocationStore();
    const {trackingNumber} = useParams<{ trackingNumber: string }>();
    const { i18n } = useTranslation();

    // 查询DSP包裹详情
    const {
        data: packageData,
        loading,
        error
    } = useQuery<GetDspPackageDetailByTrackingNumberResponse, GetDspPackageDetailByTrackingNumberVariables>(
        GET_DSP_PACKAGE_DETAIL,
        {
            variables: {
                trackingNumber: trackingNumber || ""
            },
            skip: !trackingNumber,
            fetchPolicy: 'cache-and-network'
        }
    );
    const packageDetail = packageData?.getDspPackageDetailByTrackingNumber;

    // 妥投方式 i18n 配置
    const { data: deliveryMethodI18nData } = useQuery<GetDeliveryMethodI18nConfigResponse>(
        GET_DELIVERY_METHOD_I18N_CONFIG,
        { fetchPolicy: 'cache-first' }
    );
    const deliveryMethodI18nConfig = deliveryMethodI18nData?.getDeliveryMethodI18nConfig ?? [];

    useEffect(() => {
        if (packageDetail) {
            const podImages = packageDetail?.podImageS3UrlsJson || [];
            const podImageKeys = packageDetail?.podImageS3KeysJson || [];
            
            if (showMultiPhotoSelector) {
                // 新版本：数组形式
                if (podImages.length > 0 && podImageKeys.length > 0) {
                    setPhotosArray(podImages.map((url, index) => ({
                        url,
                        objectKey: podImageKeys[index] || ''
                    })));
                }
            } else {
                // 老版本：固定位置
                if (podImages.length > 0 && podImageKeys.length > 0) {
                    setPhotos({
                        label: {
                            url: podImages[0],
                            objectKey: podImageKeys[0]
                        },
                        location: {
                            url: podImages[1],
                            objectKey: podImageKeys[1]
                        },
                        houseNumber: {
                            url: podImages[2],
                            objectKey: podImageKeys[2]
                        },
                    });
                }
            }
        }
    }, [packageDetail, showMultiPhotoSelector]);

    // 计算是否在6小时内可编辑（参考 DeliveredDetailPage 的逻辑）
    const deliveredAt = packageDetail?.deliveredAt;
    const sixHoursLater = deliveredAt ? Number(deliveredAt) + 24 * 60 * 60 : null;
    const isWithinSixHours = sixHoursLater ? timestampToDate(sixHoursLater) > timestampToDate(Date.now()) : false;
    const podReviewStatus = packageDetail?.podReviewStatus;
    // 可编辑条件：在6小时内 且 POD审核状态不是人工审核通过
    // 注意：这个页面是从运营数据页面跳转过来的，DSP管理员也应该能够修改签收凭证
    const canEdit = deliveredAt ? (isWithinSixHours && podReviewStatus !== PodReviewStatus.MANUAL_APPROVED) : false;
    
    // 调试信息（开发环境使用）
    useEffect(() => {
        if (packageDetail && process.env.NODE_ENV === 'development') {
            console.log('DspParticularsPodPage canEdit debug:', {
                deliveredAt,
                sixHoursLater,
                isWithinSixHours,
                isDspManager: isDspManager(),
                podReviewStatus,
                MANUAL_APPROVED: PodReviewStatus.MANUAL_APPROVED,
                canEdit,
                currentTime: Date.now(),
                sixHoursLaterDate: sixHoursLater ? timestampToDate(sixHoursLater).toISOString() : null
            });
        }
    }, [packageDetail, deliveredAt, isWithinSixHours, podReviewStatus, canEdit, sixHoursLater]);


    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 版本检查
    useEffect(() => {
        const checkVersion = async () => {
             const isHigher = await isAppVersionGreaterThanOrEqual(kNativeMultiImagePickerVersion);
             setShowMultiPhotoSelector(isHigher);
        };

        checkVersion();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col h-screen bg-white">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    if (!trackingNumber || error || !packageDetail) {
        return (
            <div className="p-4">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <ErrorBlock
                    status="empty"
                    title={t('pages.dsp.packageDetail.packageNotFound', '包裹不存在')}
                    description={t('pages.dsp.packageDetail.packageNotFoundDescription', '未找到该运单号对应的包裹信息')}
                />
            </div>
        );
    }

    /*照片相关操作 start======*/

    // 拍照或从相册选择 - 老版本
    const takePhoto = async (type: PositionType, file: File) => {
        try {
            setIsLoading(true);
            await photoService.handlePhotoUnUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片 - 老版本
    const deletePhoto = (type: PositionType) => {
        Dialog.confirm({
            content: t('pages.delivery.detail.deletePhotoTitle'),
            confirmText: t('pages.delivery.detail.deletePhotoDelete'),
            cancelText: t('pages.delivery.detail.deletePhotoCancel'),
            onConfirm: () => {
                photoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 删除照片 - 新版本（数组）
    const deletePhotoArray = (index: number) => {
        Dialog.confirm({
            content: t('pages.delivery.detail.deletePhotoTitle'),
            confirmText: t('pages.delivery.detail.deletePhotoDelete'),
            cancelText: t('pages.delivery.detail.deletePhotoCancel'),
            onConfirm: () => {
                multiPhotoService.handleDeletePhotoArray(index, (updateFn) => {
                    setPhotosArray((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 处理多图画廊的连拍功能（新版本数组形式）
    const handleMultiPhotoGalleryBurstCapture = () => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
        
        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;
        
        openCamera(remainingPhotoCount, minSelectionCount, t('pages.delivery.detail.requiredPhotos'), async (photos: string[]) => {
            try {
                await multiPhotoService.handleMultiPhotoSelectionArray(
                    photos,
                    (updateFn) => setPhotosArray(updateFn)
                );
            } catch (error) {
                console.error('多图片处理失败:', error);
                        Toast.show({ content: t('pages.delivery.exception.multiPhotoProcessingFailed'), position: 'center' });
            }
        });
    };

    // 完成派送
    const handleCompleteDelivery = async (): Promise<void> => {
        setIsLoading(true);

        try {
            // 验证照片
            if (showMultiPhotoSelector) {
                // 新版本：至少需要3张图片，有url即可
                if (photosArray.length < kMinSelectorPhotoLength || !photosArray.every(photo => photo.url)) {
                    Toast.show({
                        content: t('pages.delivery.detail.takeAtLeastPhotos', { count: kMinSelectorPhotoLength }),
                        position: 'center'
                    });
                    return;
                }
            } else {
                // 老版本：需要3张固定位置的图片
                const hasLabel = photos.label?.objectKey;
                const hasLocation = photos.location?.objectKey;
                const hasHouseNumber = photos.houseNumber?.objectKey;
                if (!hasLabel || !hasLocation || !hasHouseNumber) {
                    Toast.show({
                        content: t('pages.delivery.detail.takePhotosOfRequired'),
                        position: 'center'
                    });
                    return;
                }
            }

            // 获取需要上传的照片
            const photosToUpload = showMultiPhotoSelector
                ? photosArray.filter((item): item is PhotoType => item?.objectKey !== undefined && item?.url !== undefined)
                : Object.values(photos).filter((item): item is PhotoType => item?.objectKey !== undefined && item?.url !== undefined);

            // 执行照片上传
            const photoKeys = await uploadPhotosWithTimeout(photosToUpload, trackingNumber);

            // 直接调用 handleDspDeliveredEvent 方法
            const deliveredInput: DeliveredEventInput = {
                trackingNumber: trackingNumber,
                location: {
                    latitude: position?.latitude || 0.0,
                    longitude: position?.longitude || 0.0
                },
                deliveredAt: new Date().toISOString(),
                podImageS3KeysJson: photoKeys,
                id: packageDetail?.deliveryTaskOrder?.id || 0
            };
            
            await handleDspDeliveredEvent(deliveredInput);
            Toast.show({ content: t('pages.delivery.detail.deliveryCompleted'), position: 'center' });
            
            // 优先检查是否有返回参数（从运营数据页面跳转过来）
            const driverId = searchParams.get('driverId');
            const date = searchParams.get('date');
            const tabType = searchParams.get('tabType');
            const taskTabType = searchParams.get('taskTabType');
            if (taskTabType) {
                // 从任务详情页面跳转过来，返回到任务详情页面
                const taskId = searchParams.get('taskId');
                // 返回到任务详情页面，并传递tab状态
                navigate(DspPath.DELIVERY_DSP_DELIVERY_TASK_DETAILS, {
                    state: {
                        taskId: taskId ? parseInt(taskId) : 0,
                        activeTab: taskTabType
                    }
                });
            } else if (driverId && tabType) {
                // 从运营数据页面跳转过来，返回到对应的tab
                const returnParams = new URLSearchParams();
                returnParams.set('tabType', tabType);
                if (date) returnParams.set('date', date);
                navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS.replace(':driverId', driverId)}?${returnParams.toString()}`);
            } else {
                navigate(DspPath.DELIVERY_DSP_OPERATIONAL);
            }
        } catch (error: any) {
            console.error('Failed to complete delivery:', error);
            Toast.show({content: t('pages.delivery.detail.deliveryFailed'), position: 'center'});
        } finally {
            setIsLoading(false);
        }
    };

    // 图片多选 
    const multiPhotoSelectorView = (() => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
        
        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;
        
        return (
            <MultiPhotoSelectorView 
                minPhotos={minSelectionCount} 
                maxPhotos={remainingPhotoCount} 
                onPhotoSelected={async (photos: string[]) => {
                    try {
                        const result = await multiPhotoService.handleMultiPhotoSelectionArray(
                            photos,
                            (updateFn) => setPhotosArray(updateFn)
                        );
                        
                        if (!result.success) {
                            console.warn('部分图片上传失败:', result.errors);
                            // 可以显示提示信息
                        }
                    } catch (error) {
                        console.error('多图片处理失败:', error);
                    }
                }}
            />
        );
    })()

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => {
                    // 检查跳转来源并返回到相应页面
                    const driverId = searchParams.get('driverId');
                    const date = searchParams.get('date');
                    const tabType = searchParams.get('tabType');
                    const taskTabType = searchParams.get('taskTabType');

                    if (taskTabType) {
                        // 从任务详情页面跳转过来，返回到任务详情页面
                        const taskId = searchParams.get('taskId');
                        // 返回到任务详情页面，并传递tab状态
                        navigate(DspPath.DELIVERY_DSP_DELIVERY_TASK_DETAILS, {
                            state: {
                                taskId: taskId ? parseInt(taskId) : 0,
                                activeTab: taskTabType
                            }
                        });
                    } else if (driverId && tabType) {
                        // 从运营数据页面跳转过来，返回到对应的tab
                        const returnParams = new URLSearchParams();
                        returnParams.set('tabType', tabType);
                        if (date) returnParams.set('date', date);
                        navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS.replace(':driverId', driverId)}?${returnParams.toString()}`);
                    } else {
                        navigate(DspPath.DELIVERY_DSP_OPERATIONAL);
                    }
                }}>
                    {trackingNumber}
                </NavBar>
            </div>

            <div className="flex-1 overflow-y-auto pb-20">
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <div className="mb-1">
                        <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.orderInfo')}</h2>
                        <InfoRow
                            label={t('pages.dsp.packageDetail.trackingNo')}
                            value={packageDetail.trackingNumber}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.stop')}
                            value={packageDetail?.deliveryTaskOrder?.stopNumber != null ? formatStopNumber(packageDetail?.deliveryTaskOrder?.stopNumber,packageDetail?.deliveryTaskOrder?.subStopNumber) : '0'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.route')}
                            value={packageDetail?.deliveryTaskOrder?.routeName || '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.assignedTime')}
                            value={packageDetail?.deliverAt ? formatTimestampToSecondsTime(packageDetail?.deliverAt) : '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.deliveryAttempt')}
                            value={`${packageDetail?.deliveryAttempts || 0} out of 3`}
                        />
                        <OrderNoteItem 
                            orderNote={packageDetail?.deliveryTaskOrder?.orderNote}
                            currentLanguage={i18n.language}
                        />
                        {/* 根据isDelivered状态显示不同的信息 */}
                        {packageDetail?.isDelivered && (
                            <>
                                <InfoRow
                                    label={t('pages.dsp.packageDetail.deliveredAt')}
                                    value={packageDetail?.deliveredAt ? formatTimestampToSecondsTime(packageDetail?.deliveredAt) : '-'}
                                />
                                <InfoRow
                                    label={t('pages.dsp.packageDetail.deliveredBy')}
                                    value={packageDetail?.deliveredBy || '-'}
                                />
                            </>
                        )}
                        <InfoRow
                            label={t('pages.dsp.packageDetail.taskDate')}
                            value={packageDetail?.taskDate || '-'}
                        />
                        <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.customerInfo')}</h2>
                        <div>
                            <InfoRow
                                label={t('pages.dsp.packageDetail.customerName')}
                                value={packageDetail?.recipientName || '-'}
                            />
                            <InfoRow
                                label={t('pages.dsp.packageDetail.phoneNumber')}
                                value={maskPhoneNumber(packageDetail?.recipientPhone) || '-'}
                            />
                            <InfoRow
                                label={t('pages.dsp.packageDetail.address')}
                                value={packageDetail?.recipientAddress || '-'}
                            />
                            {packageDetail?.isDelivered && deliveryMethodI18nConfig.length > 0 && packageDetail.deliveryMethod && (() => {
                                const item = deliveryMethodI18nConfig.find(i => i.code === packageDetail.deliveryMethod);
                                if (!item) return null;
                                return <DeliveryMethodDisplayCard item={item} />;
                            })()}
                        </div>
                    </div>
                </div>

                {/* 照片 - 根据状态显示不同的图片 */}
                {canEdit ? (
                    <div className="bg-white p-1">
                        <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                            <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                            <p className="text-sm text-gray-600 mb-1">
                                {showMultiPhotoSelector
                                    ? t('pages.delivery.detail.photoRequirement')
                                    : t('pages.delivery.detail.photoDesc')
                                }
                            </p>

                            {!showMultiPhotoSelector && <div className="grid grid-cols-3 gap-2 my-4">
                                <PhotoUploadCard
                                    photoUrl={photos.label?.url}
                                    type="label"
                                    description={`${t('pages.delivery.detail.parcelLabel')}`}
                                    onTakePhoto={takePhoto}
                                    onDelete={deletePhoto}
                                    onView={handleViewPhoto}
                                />
                                <PhotoUploadCard
                                    photoUrl={photos.location?.url}
                                    type="location"
                                    description={`${t('pages.delivery.detail.parcelLocation')}`}
                                    onTakePhoto={takePhoto}
                                    onDelete={deletePhoto}
                                    onView={handleViewPhoto}
                                />
                                <PhotoUploadCard
                                    photoUrl={photos.houseNumber?.url}
                                    type="houseNumber"
                                    description={`${t('pages.delivery.detail.houseNumber')}`}
                                    onTakePhoto={takePhoto}
                                    onDelete={deletePhoto}
                                    onView={handleViewPhoto}
                                />
                            </div>}
                            {/* 多图画廊展示 >=1.2.0 */}
                            {showMultiPhotoSelector && <div className="mt-4">
                                <MultiPhotoGalleryView
                                    photos={photosArray
                                        .filter((photo): photo is PhotoType => photo?.url !== undefined)
                                        .map(photo => photo.url)
                                    }
                                    onDelete={(index: number) => {
                                        deletePhotoArray(index);
                                    }}
                                    maxDisplayCount={6}
                                    onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
                                />
                            </div>}
                            {/* 相册上传 >=1.2.0 */}
                            {showMultiPhotoSelector && photosArray.length < kMaxSelectorPhotoLength && <div className="mt-4">
                                {multiPhotoSelectorView}
                            </div>}
                        </div>

                        {/* 查看大图 */}
                        <PhotoViewer
                            photoUrl={viewingPhoto}
                            onClose={() => setViewingPhoto(null)}
                        />
                    </div>
                ) : (
                    <div className="bg-white p-1">
                        <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                            <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                            <div className="grid grid-cols-3 gap-2">
                                {packageDetail?.podImageS3UrlsJson && packageDetail.podImageS3UrlsJson.length > 0 ? (
                                    packageDetail.podImageS3UrlsJson.map((photo: string, index: number) => (
                                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full">
                                            <Image
                                                src={photo}
                                                alt={`Delivery photo ${index + 1}`}
                                                fit="cover"
                                                className="w-full h-full object-cover rounded-lg"
                                                onClick={() => setViewingPhoto(photo)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <>
                                        <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full flex items-center justify-center">
                                            <Image src="/404" className="w-full h-full object-cover rounded-lg"/>
                                        </div>
                                        <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full flex items-center justify-center">
                                            <Image src="/404" className="w-full h-full object-cover rounded-lg"/>
                                        </div>
                                        <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full flex items-center justify-center">
                                            <Image src="/404" className="w-full h-full object-cover rounded-lg"/>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 查看大图 */}
                        <PhotoViewer
                            photoUrl={viewingPhoto}
                            onClose={() => setViewingPhoto(null)}
                        />
                    </div>
                )}
            </div>


            {/* 提交按钮 */}
            {canEdit && (
                <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                    <Button
                        block
                        color="primary"
                        loading={isLoading}
                        disabled={showMultiPhotoSelector
                            ? (photosArray.length < kMinSelectorPhotoLength || isLoading)
                            : (!photos.label?.url || !photos.location?.url || !photos.houseNumber?.url || isLoading)
                        }
                        onClick={handleCompleteDelivery}
                    >
                        {t('pages.dsp.particulars.editPod')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default DspParticularsPodPage;
